/**
 * Salary template calculations — aligned with Backend payroll/models.py
 * calculate_final_netpay / payroll run earnings & PF / ESIC rules.
 */

export const ESI_ALLOWED_LIMIT = 21000;
export const GROSS_MONTHLY_MAX = 99999999.99;

const DEFAULT_CONFIG = {
    pfLimit: 15000,
    capAmount: 0,
    esicLimitUpto: ESI_ALLOWED_LIMIT,
    salaryDeduction: 'gross',
};

export function round2(value) {
    return Math.round((Number(value) || 0) * 100) / 100;
}

/** Keep an input empty while the user deletes the default 0. */
export function parseOptionalNumber(raw) {
    if (raw === '' || raw === null || raw === undefined) return '';
    if (raw === '-' || raw === '.' || raw === '-.') return raw;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? '' : parsed;
}

export function clampGrossMonthly(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;
    return Math.min(Math.max(num, 0), GROSS_MONTHLY_MAX);
}

function norm(name) {
    return (name || '').trim().toLowerCase();
}

function pct(value) {
    return parseFloat(value) || 0;
}

/** Percentage base for an earning row — CTC (gross) or Basic. */
function earningBase(calcof, gross, basicAmount) {
    return (calcof || 'CTC') === 'Basic' ? basicAmount : gross;
}

/** Monthly amount for one earning component. Basic is always % of gross/CTC. */
function componentMonthlyAmount(comp, gross, basicAmount) {
    const base = norm(comp.name) === 'basic'
        ? gross
        : earningBase(comp.calcof, gross, basicAmount);
    return (pct(comp.percentage) / 100) * base;
}

/** True when an earning row contributes to the CTC total (Basic is always CTC-based). */
function isCTCEarningRow(comp) {
    return norm(comp.name) === 'basic' || (comp.calcof || 'CTC') === 'CTC';
}

/** Resolve earning line amounts (Basic, DA, HRA + calcof-based others). */
export function calculateEarningsAmounts(gross, earningComponents = []) {
    const components = earningComponents || [];
    const grossVal = round2(parseFloat(gross) || 0);

    const basicComp = components.find((c) => norm(c.name) === 'basic');
    const basicAmount = basicComp ? componentMonthlyAmount(basicComp, grossVal, 0) : 0;

    const earnings = components.map((comp) => {
        const monthly = round2(componentMonthlyAmount(comp, grossVal, basicAmount));
        return {
            ...comp,
            monthly_amount: monthly,
            annual_amount: round2(monthly * 12),
        };
    });

    const ctcRows = earnings.filter((row) => isCTCEarningRow(row));
    const ctcSum = ctcRows.reduce((sum, row) => sum + Number(row.monthly_amount || 0), 0);
    const roundingDiff = round2(grossVal - ctcSum);
    if (ctcRows.length && Math.abs(roundingDiff) >= 0.005) {
        const last = ctcRows[ctcRows.length - 1];
        last.monthly_amount = round2(Number(last.monthly_amount || 0) + roundingDiff);
        last.annual_amount = round2(last.monthly_amount * 12);
    }

    const earningsMap = earnings.reduce((acc, row) => {
        acc[norm(row.name)] = row;
        return acc;
    }, {});

    // Option B: total salary = sum of "Of CTC" rows only. "Of Basic" rows (e.g. HRA, DA)
    // are a breakdown of Basic and must not inflate the total beyond gross/CTC.
    const totalEarnings = earnings
        .filter((row) => isCTCEarningRow(row))
        .reduce((sum, row) => sum + Number(row.monthly_amount || 0), 0);

    const totalCTCPercentage = components.reduce(
        (acc, curr) => (curr.calcof === 'CTC' ? acc + pct(curr.percentage) : acc),
        0,
    );

    return {
        earnings,
        earningsMap,
        basicAmount,
        daAmount: Number(earningsMap.da?.monthly_amount || 0),
        hraAmount: Number(earningsMap.hra?.monthly_amount || 0),
        totalEarnings,
        totalCTCPercentage,
    };
}

/** PF / ESIC / other deductions — mirrors payroll CustomConfiguration rules. */
export function calculateDeductionAmounts(
    gross,
    earningsMap,
    deductionComponents = [],
    config = DEFAULT_CONFIG,
) {
    const { pfLimit, capAmount, esicLimitUpto, salaryDeduction } = { ...DEFAULT_CONFIG, ...config };
    const basicAmount = Number(earningsMap.basic?.monthly_amount || 0);
    const daAmount = Number(earningsMap.da?.monthly_amount || 0);
    const deductionBase = String(salaryDeduction || 'gross').toLowerCase();
    const defaultPercentBase = deductionBase === 'ctc' && Number(config?.ctcBase)
        ? Number(config.ctcBase)
        : gross;
    const isBasicLt = basicAmount < pfLimit;
    const esiLimit = esicLimitUpto ?? ESI_ALLOWED_LIMIT;

    const deductions = (deductionComponents || []).map((comp) => {
        const name = norm(comp.name);
        let employeeShare = 0;
        let employerShare = 0;
        let monthlyTotal = 0;

        if (!comp.is_employee_share && !comp.is_employer_share) {
            monthlyTotal = round2(pct(comp.monthly_amount || comp.employee_share_monthly_amount));
            return {
                ...comp,
                is_amount: true,
                monthly_amount: monthlyTotal,
                annual_amount: round2(monthlyTotal * 12),
                employee_share_monthly_amount: monthlyTotal,
                employer_share_monthly_amount: 0,
            };
        }

        if (name === 'pf') {
            const pfBase = basicAmount + daAmount;
            if (isBasicLt) {
                employeeShare = comp.is_employee_share
                    ? (pfBase * pct(comp.employee_share_percentage)) / 100
                    : 0;
                employerShare = comp.is_employer_share
                    ? (pfBase * pct(comp.employer_share_percentage)) / 100
                    : 0;
                if (capAmount > 0) {
                    if (employeeShare >= capAmount) employeeShare = capAmount;
                    if (employerShare >= capAmount) employerShare = capAmount;
                }
            } else {
                employeeShare = comp.is_employee_share ? capAmount : 0;
                employerShare = comp.is_employer_share ? capAmount : 0;
            }
        } else if (name === 'esic' || name === 'esi') {
            if (gross <= esiLimit) {
                employeeShare = comp.is_employee_share
                    ? (gross * pct(comp.employee_share_percentage)) / 100
                    : 0;
                employerShare = comp.is_employer_share
                    ? (gross * pct(comp.employer_share_percentage)) / 100
                    : 0;
            }
        } else {
            const baseKey = norm(comp.calcof);
            let baseAmount = defaultPercentBase;
            if (baseKey === 'basic') {
                baseAmount = basicAmount + daAmount;
            } else if (baseKey === 'ctc') {
                baseAmount = defaultPercentBase;
            } else if (baseKey && earningsMap[baseKey]) {
                baseAmount = Number(earningsMap[baseKey].monthly_amount || 0);
            }
            employeeShare = comp.is_employee_share
                ? (baseAmount * pct(comp.employee_share_percentage)) / 100
                : 0;
            employerShare = comp.is_employer_share
                ? (baseAmount * pct(comp.employer_share_percentage)) / 100
                : 0;
        }

        monthlyTotal = round2(employeeShare + employerShare);

        return {
            ...comp,
            monthly_amount: monthlyTotal,
            annual_amount: round2(monthlyTotal * 12),
            employee_share_monthly_amount: round2(employeeShare),
            employer_share_monthly_amount: round2(employerShare),
        };
    });

    const totalDeductions = deductions.reduce(
        (sum, comp) => sum + Number(comp.monthly_amount || 0),
        0,
    );

    const totalEmployeeDeductions = deductions.reduce((sum, comp) => {
        const isFixed =
            comp.is_employee_share === false &&
            comp.is_employer_share === false &&
            comp.is_amount === true;
        if (isFixed) {
            return sum + Number(comp.employee_share_monthly_amount || comp.monthly_amount || 0);
        }
        return sum + Number(comp.employee_share_monthly_amount || 0);
    }, 0);

    const totalEmployerDeductions = deductions.reduce(
        (sum, comp) => sum + Number(comp.employer_share_monthly_amount || 0),
        0,
    );

    return {
        deductions,
        totalDeductions,
        totalEmployeeDeductions,
        totalEmployerDeductions,
    };
}

/** Full breakdown for preview + save payload. */
export function calculateSalaryBreakdown(
    gross,
    earningComponents = [],
    deductionComponents = [],
    config = DEFAULT_CONFIG,
) {
    const ctc = round2(parseFloat(gross) || 0);
    const earningResult = calculateEarningsAmounts(ctc, earningComponents);
    const firstPass = calculateDeductionAmounts(
        ctc,
        earningResult.earningsMap,
        deductionComponents,
        config,
    );
    const salaryDeduction = String(config?.salaryDeduction || 'gross').toLowerCase();
    const ctcBase = round2(ctc + Number(firstPass.totalEmployerDeductions || 0));
    const deductionResult = salaryDeduction === 'ctc'
        ? calculateDeductionAmounts(
            ctc,
            earningResult.earningsMap,
            deductionComponents,
            { ...config, ctcBase, salaryDeduction },
        )
        : firstPass;

    const netPayMonthly = ctc - deductionResult.totalEmployeeDeductions;

    return {
        earnings: earningResult.earnings,
        deductions: deductionResult.deductions,
        basicPay: earningResult.basicAmount,
        totalEarnings: earningResult.totalEarnings,
        totalCTCPercentage: earningResult.totalCTCPercentage,
        totalDeductions: deductionResult.totalDeductions,
        totalEmployeeDeductions: deductionResult.totalEmployeeDeductions,
        totalEmployerDeductions: deductionResult.totalEmployerDeductions,
        netPayMonthly,
        netPayAnnual: netPayMonthly * 12,
    };
}

function formatInr(value) {
    return Number(value || 0).toLocaleString('en-IN');
}

/** Human-readable PF rule from payroll configuration (for template preview). */
export function getPfCalculationHint(basicAmount, daAmount, config = DEFAULT_CONFIG) {
    const { pfLimit, capAmount } = { ...DEFAULT_CONFIG, ...config };
    const basic = Number(basicAmount) || 0;
    const da = Number(daAmount) || 0;
    const pfBase = basic + da;

    if (basic >= pfLimit) {
        if (capAmount > 0) {
            return `Basic (₹${formatInr(basic)}) is at or above the PF wage ceiling (₹${formatInr(pfLimit)} from configuration). Employee and employer PF are ₹${formatInr(capAmount)} each.`;
        }
        return `Basic (₹${formatInr(basic)}) is at or above the PF wage ceiling (₹${formatInr(pfLimit)} from configuration).`;
    }

    let hint = `PF is calculated on Basic + DA (₹${formatInr(pfBase)}) using the percentages below. Wage ceiling: ₹${formatInr(pfLimit)} (from configuration).`;
    if (capAmount > 0) {
        hint += ` Max per share when capped: ₹${formatInr(capAmount)}.`;
    }
    return hint;
}

/** Human-readable ESIC applicability from configuration. */
export function getEsicCalculationHint(gross, config = DEFAULT_CONFIG) {
    const { esicLimitUpto } = { ...DEFAULT_CONFIG, ...config };
    const limit = esicLimitUpto ?? ESI_ALLOWED_LIMIT;
    const grossVal = Number(gross) || 0;
    if (grossVal <= limit) {
        return `Monthly gross (₹${formatInr(grossVal)}) is within the ESIC limit (₹${formatInr(limit)}). ESIC applies on CTC/gross.`;
    }
    return `Monthly gross (₹${formatInr(grossVal)}) exceeds the ESIC limit (₹${formatInr(limit)}). ESIC is not deducted.`;
}

/** Row-level amounts for ComponentTable display. */
export function getComponentDisplayAmounts(
    component,
    type,
    gross,
    earningComponents,
    config = DEFAULT_CONFIG,
) {
    if (type === 'Earnings') {
        const { earnings } = calculateEarningsAmounts(gross, earningComponents);
        const row = earnings.find((e) => e.name === component.name && e.calcof === component.calcof)
            || earnings.find((e) => e.name === component.name);
        const monthly = Number(row?.monthly_amount || 0);
        return {
            monthly: monthly.toFixed(2),
            annual: (monthly * 12).toFixed(2),
        };
    }

    const { earningsMap } = calculateEarningsAmounts(gross, earningComponents);
    const { deductions } = calculateDeductionAmounts(gross, earningsMap, [component], config);
    const row = deductions[0] || {};
    return {
        monthly: Number(row.monthly_amount || 0).toFixed(2),
        annual: Number(row.annual_amount || 0).toFixed(2),
        employeeMonthly: Number(row.employee_share_monthly_amount || 0).toFixed(2),
        employerMonthly: Number(row.employer_share_monthly_amount || 0).toFixed(2),
    };
}
